import { LitElement, html, css } from 'lit';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { Download, Trash2, Upload, FileUp, FolderUp, ChevronDown, StickyNote, RefreshCw, MoreHorizontal } from 'lucide';
import { lucideIcon } from '../icon.js';
import { hallucinatiePreventie } from '../utils/fout-uitleg.js';
import { tipMove, tipHide } from '../utils/hover-tip.js';
import { api } from '../api.js';
import { confirmDialog, alertDialog, promptDialog } from '../utils/confirm-dialog.js';
import { isExtractableDoc, extractDocumentText } from '../utils/extract-text.js';
import { formatDateTime } from '../utils/datetime.js';
import { EXTRACTION_PROFILES, getExtractionProfile } from '@mv/models';

/** Eén regel in het 'Test toevoegen'-menu. */
const MENU_ITEM = 'display:flex;align-items:flex-start;gap:.6rem;width:100%;padding:.55rem .6rem;border-radius:.5rem;cursor:pointer;font-size:.88rem;color:var(--mv-text,#181c32);';
import './recording-row-detail.js'; // reuse the exact same boorprofiel viewer as /projects
import './mv-table.js';              // reuse the same expandable table as /projects
import './searchable-select.js';     // reuse the BZB-style searchable dropdown
import { inPorties } from '../utils/upload-porties.js';

/**
 * De aan/uit-schakelaars van de AI-pijplijn, zoals de testbalk ze toont. Welke er zijn en wat hun
 * standaard is, bepaalt de backend (config/aiSchakelaars.js); de API geeft er altijd een
 * true/false voor terug. Hier staat alleen hoe ze heten.
 */
const SCHAKELAARS = [
  { veld: 'speechCheck', label: 'Hallucinatie preventie', titel: 'Hallucinatie tegengaan door logisch aantal gesproken woorden per seconde te hanteren' },
  { veld: 'compactOutput', label: 'Snelle uitvoer', titel: 'Het model schrijft alleen wat het herkent; de software vult de rest aan' },
  { veld: 'priority', label: 'Priority', titel: 'OpenAI priority processing: sneller, iets duurder' },
];

/**
 * Hoeveel samples er tegelijk door de keten gaan.
 *
 * Hier stond een pauze van 2 seconden TUSSEN samples, omdat de projectsleutel op 30.000 tokens
 * per minuut zat: één extractie is er al zo'n 18.000, dus twee tegelijk liep gegarandeerd tegen
 * een 429. Die limiet staat inmiddels op 450.000, en daarmee is wachten pure verspilling — tien
 * tests duurden zo tien keer de duur van één.
 *
 * Vijf tegelijk is ruim binnen dat budget (5 x 18.000 = 90.000) en komt overeen met de
 * batchgrootte van de verwerkingswachtrij. De backend maakt per aanroep een eigen
 * ProcessingService op de validatiedatabase, dus gelijktijdige tests zitten elkaar niet in de
 * weg. Loopt het toch tegen een ratelimiet, dan wacht de backend zelf af en probeert opnieuw.
 */
const GELIJKTIJDIG = 5;
// Sentinel voor samples zonder categorie. Géén echte categorie: je kunt 'm niet kiezen,
// hernoemen of verwijderen — je komt erin door de categorie te wissen. De sectie staat
// altijd onderaan, zodat losse tests niet tussen de ingedeelde verdwijnen.
const NO_CATEGORY = 'Geen categorie';
// Startsuggesties: hiermee begint de keuzelijst van een model waarop nog niets is ingesteld.
// Verder niets bijzonders — ze zijn net zo goed te verwijderen als een zelfgemaakte categorie.
const DEFAULT_CATEGORIES = ['Format checks', 'BZB checks', 'Boringen met peilbuizen'];

/**
 * Validatie testset — AI-regressietest (sysadmin), één subpagina per extractiemodel.
 *
 * De applicatie kent vijf modellen (lagen NEN 5104, lagen NEN 6693, peilbuis, watermonster,
 * afwerking). Elk model heeft zijn eigen prompt, schema én testset: een sample hoort bij
 * precies één model, want dezelfde audio hoeft niet op meerdere modellen te draaien. Deze
 * pagina toont dus altijd één model — welk model staat in `profile` (uit de URL).
 *
 * Per model: upload sample-opnamen, markeer het correcte resultaat als "voorbeeld", en draai
 * on-demand een test die elke sample opnieuw verwerkt en met dat voorbeeld vergelijkt.
 * Sequentieel met 2s ertussen → respecteert de rate limit.
 */
export class ValidationPage extends LitElement {
  static properties = {
    profile: { type: String },                   // het extractiemodel van deze subpagina
    samples: { type: Array, state: true },
    running: { type: String, state: true },      // '', 'process', 'test'
    runningCat: { type: String, state: true },    // category currently running (for per-category UI)
    progress: { type: Object, state: true },     // { current, total, label }
    report: { type: Object, state: true },       // { results[], passed, total } — live run only
    expandedId: { type: String, state: true },
    detail: { type: Object, state: true },       // boorprofiel of expanded sample
    _audioUrl: { type: String, state: true },     // object URL for the expanded sample's audio
    _detailTab: { type: String, state: true },    // 'current' | 'golden' — boorprofiel compare tab
    _collapsed: { type: Object, state: true },     // { [category]: true } — collapsed sections
    _addMenuOpen: { type: Boolean, state: true },   // keuzemenu van 'Test toevoegen'
    _catMenuOpen: { type: String, state: true },   // categorie waarvan het ⋯-menu openstaat
    _aiConfig: { type: Object, state: true },       // { transcriptionModel, extractionModel, promptVersionId } for test runs
    _modelOptions: { type: Object, state: true },   // server allowlists
    _promptVersions: { type: Array, state: true },  // [{id,title,isActive}]
    _transcriptPromptVersions: { type: Array, state: true },  // woordenlijsten voor audio → tekst
    loading: { type: Boolean, state: true },
  };

  static styles = css`
    :host { display:block; padding:1.5rem 2rem; color:var(--mv-text,#181c32); }
    h1 { font-size:1.5rem; margin:0 0 .25rem; }
    .sub { color:var(--mv-text-sub,#5e6278); margin:0 0 1.25rem; font-size:.95rem; }
    .bar { display:flex; flex-wrap:wrap; gap:.75rem; align-items:center; margin-bottom:1rem; }
    .action { display:inline-flex; align-items:center; gap:.35rem; padding:.5rem 1rem; border-radius:.5rem;
      border:1px solid var(--mv-border,#eff2f5); background:var(--mv-surface,#fff); color:var(--mv-text);
      font-weight:600; cursor:pointer; font-size:.9rem; }
    .primary { background:var(--mv-primary,#4b9d35); color:#fff; border-color:transparent; }
    .danger { color:var(--mv-danger,#f1416c); }
    button:disabled { opacity:.5; cursor:not-allowed; }
    .seg { display:inline-flex; border:1px solid var(--mv-border,#eff2f5); border-radius:.5rem; overflow:hidden; }
    .seg button { padding:.45rem .8rem; border:none; background:var(--mv-surface,#fff); cursor:pointer; font-size:.85rem; color:var(--mv-text-sub); }
    .seg button.on { background:var(--mv-primary,#4b9d35); color:#fff; }
    /* Subpagina's: één tabblad per extractiemodel. */
    .tabs { display:flex; flex-wrap:wrap; gap:.15rem; border-bottom:1px solid var(--mv-border,#eff2f5); }
    .tab { padding:.6rem 1rem; font-size:.9rem; font-weight:600; color:var(--mv-text-sub,#5e6278);
      text-decoration:none; border-bottom:2px solid transparent; margin-bottom:-1px; white-space:nowrap; }
    .tab:hover { color:var(--mv-text,#181c32); background:var(--mv-surface-alt,#f8f9fa); }
    .tab.on { color:var(--mv-primary,#4b9d35); border-bottom-color:var(--mv-primary,#4b9d35); }
    .note { background:#f6c34314; border:1px solid #f6c34355; color:var(--mv-text,#181c32);
      border-radius:.5rem; padding:.7rem .9rem; font-size:.83rem; line-height:1.5; margin:0 0 1rem; }
    /* Category group: one card per category, header band on top of its table. */
    /* Geen overflow:hidden op de kaart: het ⋯-menu staat in de kop en steekt eronder uit, en
       werd daardoor binnen de kaart afgeknipt. De ronde hoeken regelt nu elk onderdeel zelf --
       de kop zijn bovenkant, de tabel zijn onderkant. */
    .cat-card { border:1px solid var(--mv-border,#eff2f5); border-radius:.75rem; overflow:visible;
      box-shadow:var(--mv-shadow,0 8px 24px rgba(82,63,105,.06)); margin:1.4rem 0; background:var(--mv-surface,#fff); }
    .cat-head { padding:.85rem 1.25rem; background:var(--mv-surface-alt,#f8f9fa); border-bottom:1px solid var(--mv-border,#eff2f5);
      border-radius:.75rem .75rem 0 0; }
    /* Ingeklapt is de kop het enige dat je ziet; dan rondt hij alle vier de hoeken af. */
    .cat-card.collapsed .cat-head { border-bottom:none; border-radius:.75rem; }
    .cat-title { display:flex; align-items:center; gap:.6rem; flex-wrap:wrap; }
    .cat-title h2 { font-size:1.05rem; margin:0; color:var(--mv-text,#181c32); }
    .chevron { border:none; background:none; cursor:pointer; font-size:1rem; line-height:1;
      color:var(--mv-text-sub,#5e6278); padding:.15rem .25rem; border-radius:.3rem; }
    .chevron:hover { background:var(--mv-border,#eff2f5); }
    /* Neutralise the inner table's own card so it reads as part of this group. */
    .cat-card mv-table { border:none; box-shadow:none; background:transparent; display:block;
      overflow:hidden; border-radius:0 0 .75rem .75rem; }
    table { width:100%; border-collapse:collapse; font-size:.9rem; }
    th,td { text-align:left; padding:.55rem .6rem; border-bottom:1px solid var(--mv-border,#eff2f5); vertical-align:top; }
    th { color:var(--mv-text-sub,#5e6278); font-size:.72rem; letter-spacing:.04em; text-transform:uppercase; }
    .pill { display:inline-block; padding:1px 8px; border-radius:6px; font-size:.7rem; font-weight:800; }
    .pill.gold { background:#f6c34322; color:#a9821a; border:1px solid #f6c34355; }
    .pill.ok { background:#4b9d3522; color:#3d812b; }
    .pill.bad { background:#f1416c22; color:#f1416c; }
    .pill.muted { background:var(--mv-border,#eff2f5); color:var(--mv-text-sub); }
    .report { border:1px solid var(--mv-border,#eff2f5); border-radius:.625rem; padding:1rem 1.25rem; margin-bottom:1.25rem; }
    .report h3 { margin:0 0 .5rem; }
    .mismatch { font-family:ui-monospace,monospace; font-size:.8rem; color:var(--mv-danger,#f1416c); margin:.1rem 0; }
    .prof { background:var(--mv-surface-alt,#f8f9fa); border-radius:.5rem; padding:.75rem; font-size:.82rem; white-space:pre-wrap; }
    .muted { color:var(--mv-text-sub,#5e6278); }
    .drop { border:2px dashed var(--mv-border,#d8dbe0); border-radius:.625rem; padding:1rem; text-align:center; color:var(--mv-text-sub); cursor:pointer; }
  `;

  constructor() {
    super();
    this.profile = 'nen5104';
    this.samples = [];
    this.running = '';
    this.runningCat = '';
    this.progress = null;
    this.report = null;
    this.expandedId = '';
    this.detail = null;
    this._detailTab = 'current';
    this._collapsed = this._loadCollapsed();
    this._known = this._loadKnown();
    this._aiConfig = { transcriptionModel: '', extractionModel: '', promptVersionId: '' };

    this._modelOptions = { transcriptionModels: [], extractionModels: [] };
    this._promptVersions = [];
    this._transcriptPromptVersions = [];
    this.loading = true;
  }

  /** De definitie (label, domein, standaard) van het model van deze subpagina. */
  get _profile() { return getExtractionProfile(this.profile); }

  /**
   * Welke boring-tabbladen dit model laat zien. Een domeinmodel schrijft precies één slice,
   * dus daar hoort ook één tabblad bij; het lagen-model bouwt de hele boring en houdt alles.
   */
  _profileTabs() {
    return {
      peilbuis: ['peilbuizen'],
      watermonster: ['watermonster'],
      afwerking: ['afwerking'],
    }[this._profile.domain] || null;
  }

  connectedCallback() {
    super.connectedCallback();
    this.loadSamples();
    this._loadModelConfig();
    // Klik ergens anders → menu dicht. De knop zelf stopt de bubbling, dus die valt hierbuiten.
    this._closeMenus = () => {
      if (this._addMenuOpen) this._addMenuOpen = false;
      if (this._catMenuOpen) this._catMenuOpen = '';
    };
    document.addEventListener('click', this._closeMenus);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._closeMenus);
    super.disconnectedCallback();
  }

  /** Wisselen van model = een andere testset: alles opnieuw laden en de rij-state resetten. */
  updated(changed) {
    if (!changed.has('profile') || changed.get('profile') === undefined) return;
    this.samples = [];
    this.report = null;
    this.expandedId = '';
    this.detail = null;
    this._revokeAudio();
    this._collapsed = this._loadCollapsed();
    this._known = this._loadKnown();
    this.loadSamples();
    this._loadModelConfig();
  }

  /** Load model options + prompt versions VAN DIT MODEL; default the test config to production. */
  async _loadModelConfig() {
    const profile = this.profile;
    try {
      // De transcriptieprompt hoort bij geen enkel model: hij bepaalt hoe audio tekst wordt.
      // Eén lijst dus, op elk tabblad hetzelfde.
      const [opt, cfg, pv, tpv] = await Promise.all([
        api.getModelOptions(), api.getModelConfig(),
        api.listPromptVersions(profile), api.listPromptVersions(`transcriptie-${profile}`),
      ]);
      if (this.profile !== profile) return; // ondertussen van model gewisseld
      this._modelOptions = opt || this._modelOptions;
      this._promptVersions = pv?.versions || [];
      this._transcriptPromptVersions = tpv?.versions || [];
      this._prodConfig = cfg || null;
      /*
       * Wat je op DEZE pagina het laatst koos, per model bewaard. De balk is de config van je
       * testrun; die hoort te blijven staan tot je hem zelf verzet, ook na een herlaad.
       * Wat er in productie draait lees je op de regel eronder.
       *
       * Hier inlezen en niet in de constructor: daar is `this.profile` nog niet gezet, en dan
       * schrijf je onder een andere sleutel dan je leest.
       */
      const o = this._loadKeuze();
      const eigen = (veld, serverwaarde) => (veld in o ? (o[veld] ?? '') : (serverwaarde || ''));
      this._aiConfig = {
        transcriptionModel: eigen('transcriptionModel', cfg?.transcriptionModel),
        extractionModel: eigen('extractionModel', cfg?.extractionModel),
        // De promptversie hoort bij dit model, dus die komt altijd vers uit de config.
        promptVersionId: cfg?.promptVersionIds?.[profile] || '',
        // Mag leeg blijven: zonder gekozen versie gaat er geen woordenlijst mee.
        transcriptionPromptVersionId: eigen('transcriptionPromptVersionId', cfg?.transcriptionPromptVersionIds?.[profile]),
        // De aan/uit-schakelaars voor je testrun; zonder eigen keuze volgen ze productie.
        ...Object.fromEntries(SCHAKELAARS.map(({ veld }) => [veld, veld in o ? o[veld] === true : cfg?.[veld] === true])),
      };
    } catch (_) { /* header hides; runs fall back to production config server-side */ }
  }

  _navigate(path) {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }

  // ── Model/prompt header (drives the validation test runs) ──────────────────
  _renderModelBar() {
    const o = this._modelOptions || {};
    const c = this._aiConfig || {};
    const selStyle = "-webkit-appearance:none;appearance:none;padding:.45rem 1.7rem .45rem .6rem;border:1px solid var(--mv-border,#eff2f5);border-radius:.5rem;background-color:var(--mv-surface,#fff);color:var(--mv-text,#181c32);font-size:.82rem;font-family:inherit;cursor:pointer;outline:none;background-image:url(\"data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20width='12'%20height='12'%20viewBox='0%200%2024%2024'%20fill='none'%20stroke='%236c757d'%20stroke-width='2.5'%20stroke-linecap='round'%20stroke-linejoin='round'%3E%3Cpolyline%20points='6%209%2012%2015%2018%209'/%3E%3C/svg%3E\");background-repeat:no-repeat;background-position:right 8px center;background-size:12px;";
    const lbl = 'font-size:.65rem;font-weight:800;letter-spacing:.04em;text-transform:uppercase;color:var(--mv-text-sub,#5e6278);margin-bottom:2px;';
    const item = (label, body) => html`<div style="display:flex;flex-direction:column;"><span style=${lbl}>${label}</span>${body}</div>`;
    const modelSelect = (field, opts) => html`
      <select style=${selStyle} @change=${(e) => this._setOverride(field, e.target.value)}>
        ${(opts || []).map(m => html`<option value=${m} ?selected=${c[field] === m}>${m}</option>`)}
      </select>`;
    // Een aan/uit-schakelaar uit SCHAKELAARS als keuzelijst. De hallucinatie preventie staat bij
    // de transcriptie, snelle uitvoer en priority bij de extractie.
    const aanUit = (veld) => {
      const s = SCHAKELAARS.find((x) => x.veld === veld);
      return item(s.label, html`
        <select style=${selStyle} title=${s.titel} @change=${(e) => this._setOverride(veld, e.target.value === 'aan')}>
          <option value="aan" ?selected=${c[veld] === true}>Aan</option>
          <option value="uit" ?selected=${c[veld] !== true}>Uit</option>
        </select>`);
    };
    const titles = (this._promptVersions || []).map(v => v.title);
    const activeTitle = (this._promptVersions || []).find(v => v.id === c.promptVersionId)?.title || '';
    const tTitles = (this._transcriptPromptVersions || []).map(v => v.title);
    const tActiveTitle = (this._transcriptPromptVersions || []).find(v => v.id === c.transcriptionPromptVersionId)?.title || '';
    return html`
      ${item('Audio → tekst', modelSelect('transcriptionModel', o.transcriptionModels))}
      ${item('Transcript-prompt', html`
        <searchable-select style="min-width:150px;"
          .options=${tTitles} .value=${tActiveTitle}
          optionAction="Prompt bewerken"
          addLabel="+ Nieuwe transcript-prompt…"
          @change=${this._onTranscriptPromptChange}
          @action=${this._onTranscriptPromptEdit}
          @add=${this._onTranscriptPromptCreate}></searchable-select>`)}
      ${aanUit('speechCheck')}
      ${item(`Tekst → ${this._profile.domain === 'lagen' ? 'boorprofiel' : this._profile.short.toLowerCase()}`, modelSelect('extractionModel', o.extractionModels))}
      ${aanUit('compactOutput')}
      ${aanUit('priority')}

      ${item('Prompt', html`
        <searchable-select style="min-width:150px;"
          .options=${titles} .value=${activeTitle}
          optionAction="Prompt bewerken"
          addLabel="+ Nieuwe promptversie…"
          @change=${this._onPromptChange}
          @action=${this._onPromptEdit}
          @add=${this._onPromptCreate}></searchable-select>`)}
    `;
  }

  /**
   * Wat er voor DIT model in productie draait, als leesregel onder de keuzes.
   *
   * De balk erboven is de config van je testrun en die mag je vrij verzetten; die keuze blijft
   * ook staan na een herlaad. Zonder deze regel weet je een week later niet meer of je een
   * afwijking bekijkt of gewoon productie — en dan lees je een testuitslag alsof hij over
   * productie ging. Namen in plaats van een waarschuwing: jij ziet zelf wel wat afwijkt.
   */
  _renderProductieRegel() {
    const p = this._prodConfig;
    if (!p) return '';
    const titel = (lijst, id) => (id ? ((lijst || []).find(v => v.id === id)?.title || 'onbekend') : '—');
    const uit = this._profile.domain === 'lagen' ? 'boorprofiel' : this._profile.short.toLowerCase();
    const del = (label, waarde) => html`<span><span style="color:var(--mv-text-muted,#a1a5b7);">${label}</span> ${waarde}</span>`;
    return html`
      <div style="display:flex;flex-wrap:wrap;gap:.35rem 1.1rem;margin-top:.5rem;font-size:.75rem;color:var(--mv-text-sub,#5e6278);">
        <span style="font-weight:700;">In productie:</span>
        ${del('audio → tekst', p.transcriptionModel || '—')}
        ${del('transcript-prompt', titel(this._transcriptPromptVersions, (p.transcriptionPromptVersionIds || {})[this.profile]))}
        ${SCHAKELAARS.map((s) => del(s.label.toLowerCase(), p[s.veld] ? 'aan' : 'uit'))}
        ${del(`tekst → ${uit}`, p.extractionModel || '—')}
        ${del('prompt', titel(this._promptVersions, (p.promptVersionIds || {})[this.profile]))}
      </div>`;
  }

  _promptByTitle(title) { return (this._promptVersions || []).find(v => v.title === title) || null; }
  _transcriptPromptByTitle(title) { return (this._transcriptPromptVersions || []).find(v => v.title === title) || null; }

  _onTranscriptPromptChange(e) {
    const v = this._transcriptPromptByTitle(e.detail.value);
    this._setOverride('transcriptionPromptVersionId', v ? v.id : '');
  }

  _onTranscriptPromptEdit(e) {
    const v = this._transcriptPromptByTitle(e.detail.value);
    if (v) this._navigate(`/admin/validation/model/${v.id}`);
  }

  async _onTranscriptPromptCreate() {
    const name = await promptDialog({
      title: 'Nieuwe transcript-prompt',
      message: 'Naam van de nieuwe transcript-prompt:',
      placeholder: 'bijv. V0.1',
      confirmLabel: 'Aanmaken',
    });
    if (!name || !name.trim()) return;
    try {
      // Voorgevuld met de tekst van de nu gekozen versie, zodat je verder kunt waar je was.
      let baseText = '';
      const cur = this._aiConfig?.transcriptionPromptVersionId;
      if (cur) { const v = await api.getPromptVersion(cur); baseText = v.systemPrompt || ''; }
      const created = await api.createPromptVersion({
        title: name.trim(),
        systemPrompt: baseText || 'Woordenlijst voor de transcriptie — vul hier de termen in die vaak voorkomen.',
        profile: `transcriptie-${this.profile}`,
        basedOnId: cur || undefined,
      });
      await this._loadModelConfig();
      this._navigate(`/admin/validation/model/${created.id}`);
    } catch (e) { alertDialog({ title: 'Fout', message: e.message }); }
  }


  _onPromptChange(e) {
    const v = this._promptByTitle(e.detail.value);
    if (v) this._aiConfig = { ...this._aiConfig, promptVersionId: v.id };
  }

  _onPromptEdit(e) {
    const v = this._promptByTitle(e.detail.value);
    if (v) this._navigate(`/admin/validation/model/${v.id}`);
  }

  async _onPromptCreate() {
    const name = await promptDialog({ title: 'Nieuwe promptversie', message: `Naam van de nieuwe ${this._profile.label}-promptversie:`, placeholder: 'bijv. V0.2', confirmLabel: 'Aanmaken' });
    if (!name || !name.trim()) return;
    try {
      // Seed the new version with the currently-selected prompt's text so you can tweak it.
      let baseText = '';
      if (this._aiConfig.promptVersionId) { const cur = await api.getPromptVersion(this._aiConfig.promptVersionId); baseText = cur.systemPrompt || ''; }
      const created = await api.createPromptVersion({ title: name.trim(), systemPrompt: baseText || 'Nieuwe prompt — vul hier de system prompt in.', profile: this.profile, basedOnId: this._aiConfig.promptVersionId || undefined });
      await this._loadModelConfig();
      this._navigate(`/admin/validation/model/${created.id}`);
    } catch (e) { alertDialog({ title: 'Fout', message: e.message }); }
  }

  async loadSamples() {
    this.loading = true;
    const profile = this.profile;
    try {
      const res = await api.getValidationSamples(profile);
      if (this.profile !== profile) return; // ondertussen van model gewisseld
      const list = res.samples || [];
      // Houd de uitgeklapte rij open na een reload (voorkomt inklappen bij verwerken/bevestigen).
      if (this.expandedId) { const r = list.find(x => x.id === this.expandedId); if (r) r.expanded = true; }
      this.samples = list;
    } catch (e) {
      alertDialog({ title: 'Fout', message: `Samples laden mislukt: ${e.message}` });
    } finally {
      this.loading = false;
    }
  }

  async _onFiles(e, parentRecordingId = null) {
    const raw = Array.from(e.target.files || []);
    e.target.value = '';
    if (!raw.length) return;
    this.loading = true;
    try {
      // Documents (txt/pdf/docx) → extract their text in the browser and upload that
      // as the transcript; audio is uploaded as-is for server-side transcription.
      const files = [];
      const docErrors = [];
      for (const f of raw) {
        if (isExtractableDoc(f.name)) {
          let text = '';
          try { text = await extractDocumentText(f); } catch (err) { console.error('extractie mislukt', f.name, err); }
          if (!text || !text.trim()) { docErrors.push(`Geen tekst gevonden in "${f.name}".`); continue; }
          files.push(new File([text], f.name, { type: 'text/plain' }));
        } else {
          files.push(f);
        }
      }
      if (docErrors.length) alertDialog({ title: 'Geen tekst gevonden', message: docErrors.join('\n') });
      if (!files.length) return;
      // De sample wordt getagd met het model van deze subpagina.
      // In porties, net als in het project: zie utils/upload-porties.js. Een validatieset van
      // zestig opnamen in één request loopt tegen het geheugen van de Worker aan.
      let gelukt = 0;
      try {
        for (const portie of inPorties(files)) {
          await api.uploadValidationFiles(portie, parentRecordingId, this.profile);
          gelukt += portie.length;
        }
      } catch (e) {
        if (gelukt) {
          await this.loadSamples();
          throw new Error(`${gelukt} van ${files.length} bestanden zijn geüpload; daarna ging het mis: ${e.message}`);
        }
        throw e;
      }
      await this.loadSamples();
      if (parentRecordingId && this.expandedId === parentRecordingId) await this._reloadDetail();
    } catch (err) {
      alertDialog({ title: 'Upload mislukt', message: err.message });
    } finally { this.loading = false; }
  }

  /**
   * Werk een lijst af met een vast aantal tegelijk, en meld elke keer dat er eentje klaar is.
   *
   * Niet `Promise.all` op alles tegelijk: bij een categorie van veertig samples zou dat veertig
   * extracties in één klap zijn, en dan loopt het alsnog tegen de ratelimiet. Een vast aantal
   * werkers die uit dezelfde stapel pakken houdt de druk constant, ook als de ene sample veel
   * langer duurt dan de andere.
   *
   * De uitkomsten komen op hun OORSPRONKELIJKE plek in de lijst terug, niet in de volgorde
   * waarin ze klaar zijn — anders springt het verslag door elkaar bij elke herhaling.
   */
  async _parallel(items, werk, bijElkeKlaar) {
    const uit = new Array(items.length);
    let volgende = 0, klaar = 0;
    const werker = async () => {
      while (true) {
        const i = volgende++;
        if (i >= items.length) return;
        uit[i] = await werk(items[i], i);
        klaar++;
        bijElkeKlaar?.(klaar, uit[i], items[i]);
      }
    };
    await Promise.all(Array.from({ length: Math.min(GELIJKTIJDIG, items.length) }, werker));
    return uit;
  }

  /** Process every sample in a category. */
  async processCategory(cat) {
    const todo = this.samples.filter(s => this._catOf(s) === cat);
    if (!todo.length) return;
    this.running = 'process'; this.runningCat = cat; this.report = null;
    this.progress = { current: 0, total: todo.length, label: `Verwerken: ${todo.length} samples, ${Math.min(GELIJKTIJDIG, todo.length)} tegelijk` };
    try {
      await this._parallel(
        todo,
        async (s2) => { try { await api.processValidationSample(s2.id, 'full', this._aiConfig); } catch (_) { /* door */ } },
        (klaar) => { this.progress = { current: klaar, total: todo.length, label: `Verwerken: ${klaar} van ${todo.length} klaar` }; },
      );
      await this.loadSamples();
    } finally { this.running = ''; this.runningCat = ''; this.progress = null; }
  }

  /** Run the regression test for every golden sample in a category. */
  async testCategory(cat) {
    const golden = this.samples.filter(s => this._catOf(s) === cat && s.hasGolden);
    if (!golden.length) { alertDialog({ title: 'Geen verwacht antwoord', message: `Markeer eerst minstens één sample in "${cat}" als correct.` }); return; }
    this.running = 'test'; this.runningCat = cat; this.report = null;
    let passed = 0, failed = 0;
    this.progress = { current: 0, total: golden.length, passed, failed, label: `Testen: ${golden.length} samples, ${Math.min(GELIJKTIJDIG, golden.length)} tegelijk` };
    try {
      const results = await this._parallel(
        golden,
        async (g) => {
          try { return await api.testValidationSample(g.id, 'full', this._aiConfig); }
          catch (e) { return { id: g.id, filename: g.filename, match: false, error: e.message, mismatches: [] }; }
        },
        (klaar, r) => {
          if (r?.match) passed++; else failed++;
          this.progress = { current: klaar, total: golden.length, passed, failed, label: `Testen: ${klaar} van ${golden.length} klaar` };
        },
      );
      this.report = { results, passed: results.filter((r) => r.match).length, total: results.length };
      await this.loadSamples();
    } finally { this.running = ''; this.runningCat = ''; this.progress = null; }
  }

  /**
   * Een rij is losgelaten op een andere rij: nieuwe volgorde, en mogelijk een andere categorie.
   *
   * `cat` is de categorie van de TABEL waarin is losgelaten, niet die van de gesleepte rij —
   * daarmee is verslepen tussen twee categorieën hetzelfde geval als binnen één.
   *
   * De hele volgorde van de doelcategorie gaat in één keer mee. Alleen de verplaatste rij een
   * nummer geven zou botsen met wat er al stond, en dan is de volgorde na een verversing weer
   * anders dan wat je zag.
   */
  async _onRowDrop(e, cat) {
    const { draggedId, targetId, before } = e.detail || {};
    if (!draggedId || !targetId || draggedId === targetId) return;

    const doel = this.samples.filter(x => this._catOf(x) === cat && x.id !== draggedId).map(x => x.id);
    const bij = doel.indexOf(targetId);
    if (bij < 0) return;
    doel.splice(before ? bij : bij + 1, 0, draggedId);

    // Meteen tonen; de server bevestigt daarna. Wachten op het netwerk maakt slepen stroperig.
    const volgorde = new Map(doel.map((id, i) => [id, i]));
    this.samples = this.samples.map(x => (volgorde.has(x.id)
      ? { ...x, category: cat === NO_CATEGORY ? null : cat, sortOrder: volgorde.get(x.id) }
      : x));

    try {
      await api.reorderValidationSamples(cat === NO_CATEGORY ? null : cat, doel);
      await this.loadSamples();
    } catch (err) {
      alertDialog({ title: 'Verplaatsen mislukt', message: err.message });
      await this.loadSamples();   // terug naar wat de server weet
    }
  }

  /** Zet (of wis, met null) de categorie van een sample. */
  async setCategory(s, category) {
    try { await api.setValidationCategory(s.id, category || null); await this.loadSamples(); }
    catch (e) { alertDialog({ title: 'Fout', message: e.message }); }
  }

  /** Confirm the (edited) profile as the verified expected answer (golden). */
  async markGolden(s) {
    try { await api.setValidationGolden(s.id); await this.loadSamples(); if (this.expandedId === s.id) { this._detailTab = 'golden'; await this._reloadDetail(); } }
    catch (e) { alertDialog({ title: 'Fout', message: e.message }); }
  }
  /** Wipe the example → back to "Maak voorbeeld aan" state. */
  async clearGolden(s) {
    try { await api.clearValidationGolden(s.id); await this.loadSamples(); if (this.expandedId === s.id) { this._detailTab = 'current'; await this._reloadDetail(); } }
    catch (e) { alertDialog({ title: 'Fout', message: e.message }); }
  }
  /** "Maak voorbeeld aan": process the recording into an editable draft profile. */
  async processOne(s) {
    this.running = 'process'; this.runningCat = this._catOf(s); this.progress = { current: 1, total: 1, label: `Verwerken: ${s.filename}` };
    try { await api.processValidationSample(s.id, 'full', this._aiConfig); await this.loadSamples(); if (this.expandedId === s.id) await this._reloadDetail(); }
    catch (e) { alertDialog({ title: 'Fout', message: e.message }); }
    finally { this.running = ''; this.runningCat = ''; this.progress = null; }
  }
  /**
   * Het concept-voorbeeld opnieuw opbouwen uit hetzelfde transcript.
   *
   * Zodra je aan de prompt, het model of de transcript-prompt sleutelt, is het profiel dat er
   * staat met de vórige instelling gemaakt. Zonder deze knop moest je de rij verwijderen en
   * opnieuw uploaden om dat te zien.
   *
   * Vraagt om bevestiging als er al een concept staat: dat kan met de hand gecorrigeerd zijn,
   * en opnieuw verwerken gooit die correcties weg. Het bevestigde voorbeeld (Verwacht) blijft
   * hoe dan ook staan -- dat wordt hier niet aangeraakt.
   */
  async reprocessOne(s) {
    if (s.hasProfile) {
      const ok = await confirmDialog({
        title: 'Opnieuw verwerken',
        message: `Het concept-boorprofiel van "${s.boringName || s.filename}" wordt opnieuw opgebouwd uit het transcript. `
          + 'Handmatige correcties in dat concept gaan verloren.'
          + (s.hasGolden ? ' Het bevestigde voorbeeld blijft ongewijzigd.' : ''),
        confirmLabel: 'Opnieuw verwerken',
      });
      if (!ok) return;
    }
    await this.processOne(s);
  }

  /** "Test uitvoeren" for one row: reprocess + diff against the example. */
  async testRow(s) {
    this.running = 'test'; this.runningCat = this._catOf(s); this.progress = { current: 1, total: 1, label: `Testen: ${s.filename}`, passed: 0, failed: 0 };
    try {
      const r = await api.testValidationSample(s.id, 'full', this._aiConfig);
      this.progress = { current: 1, total: 1, label: `Testen: ${s.filename}`, passed: r?.match ? 1 : 0, failed: r?.match ? 0 : 1 };
      await this.loadSamples();
      if (this.expandedId === s.id) { this._detailTab = 'current'; await this._reloadDetail(); }
    } catch (e) { alertDialog({ title: 'Fout', message: e.message }); }
    finally { this.running = ''; this.runningCat = ''; this.progress = null; }
  }
  /**
   * Download één test. De browser krijgt een blob, dus we bouwen zelf even een link — anders
   * opent de JSON in een tabblad in plaats van in je downloadmap te belanden.
   */
  async downloadSample(s) {
    try {
      const { blob, filename } = await api.downloadValidationSample(s.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (e) {
      alertDialog({ title: 'Downloaden mislukt', message: e.message });
    }
  }

  /**
   * Download een hele categorie: alle samples mét voorbeeld in één bestand. Bedoeld om een
   * testset in één keer naar een andere omgeving te tillen; los per test kan ook nog.
   */
  async downloadCategory(cat) {
    const isNone = cat === NO_CATEGORY;
    try {
      const { blob, filename } = await api.downloadValidationCategory(this.profile, isNone ? '' : cat);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (e) {
      alertDialog({ title: 'Downloaden mislukt', message: e.message });
    }
  }

  /**
   * Een gedownloade categorie terugzetten. Elk sample wordt een nieuwe rij, onder de categorie
   * uit het bestand — daarom hier geen naamvraag per test: dat zijn er bij een categorie te
   * veel, en ze staan al bij elkaar onder hun eigen kopje.
   */
  async _onImportCategory(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    this.loading = true;
    const meldingen = [];
    try {
      for (const f of files) {
        try {
          const json = JSON.parse(await f.text());
          // Iemand kiest hier vroeg of laat een losse test; die dan gewoon importeren is
          // vriendelijker dan een foutmelding over het bestandsformaat.
          if (json.geovoiceValidationSample === 1) {
            await api.importValidationSample(json);
            meldingen.push(`${f.name}: losse test geïmporteerd`);
            continue;
          }
          const res = await api.importValidationCategory(json);
          const n = res.imported?.length || 0;
          meldingen.push(`${json.category || 'Geen categorie'}: ${n} sample${n === 1 ? '' : 's'} geïmporteerd`);
          for (const bad of (res.failed || [])) meldingen.push(`  ✗ ${bad.name}: ${bad.error}`);
        } catch (err) {
          meldingen.push(`${f.name}: ${err.message}`);
        }
      }
      await this.loadSamples();
    } finally {
      this.loading = false;
    }
    if (meldingen.length) alertDialog({ title: 'Categorie importeren', message: meldingen.join('\n') });
  }

  /** Eerder gedownloade tests terugzetten. Meerdere tegelijk mag; ze zijn onafhankelijk. */
  async _onImport(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    this.loading = true;
    const failed = [];
    let ok = 0;
    try {
      for (const f of files) {
        try {
          const json = JSON.parse(await f.text());
          // Naam vragen, voorgevuld met die uit het bestand. Twee omgevingen hebben allebei
          // hun eigen namen, en zonder deze stap krijg je een tweede rij die precies zo heet
          // als een bestaande — waarna je ze niet meer uit elkaar houdt.
          const name = await promptDialog({
            title: 'Test importeren',
            message: `Onder welke naam wil je deze test opslaan?${json.golden ? ' Het bevestigde voorbeeld komt mee.' : ''}`,
            value: json.name || json.primary?.filename || '',
            confirmLabel: 'Importeren',
          });
          if (name === null) continue;   // geannuleerd
          await api.importValidationSample({ ...json, name: String(name).trim() || undefined });
          ok++;
        } catch (err) {
          failed.push(`${f.name}: ${err.message}`);
        }
      }
      await this.loadSamples();
    } finally {
      this.loading = false;
    }
    if (failed.length) {
      alertDialog({
        title: ok ? `${ok} geïmporteerd, ${failed.length} mislukt` : 'Importeren mislukt',
        message: failed.join('\n'),
      });
    }
  }

  /**
   * De notitie bij een sample bekijken en bewerken.
   *
   * Waarom is deze test nuttig, welk principe bewaakt hij. Een testset groeit, en over een
   * half jaar weet niemand meer waarom "memo_1787873133067.m4a" erin zit -- ook degene die
   * hem toevoegde niet. De notitie reist mee bij het downloaden, zodat die reden niet aan
   * deze database vastzit.
   */
  async openNote(s) {
    const res = await promptDialog({
      title: s.boringName ? `Notitie — ${s.boringName}` : 'Notitie',
      message: s.filename,
      placeholder: 'Waarom is dit een nuttige test? Welk principe bewaakt hij?',
      value: s.note || '',
      confirmLabel: 'Opslaan',
      multiline: true,
      // Verwijderen alleen aanbieden als er iets te verwijderen is.
      deleteLabel: s.note ? 'Verwijderen' : '',
    });
    // null = weggeklikt of geannuleerd; dan gebeurt er niets. Alleen opslaan (ook van een leeg
    // veld) en de verwijderknop leveren een string op, en die is dan de nieuwe waarde.
    if (res === null) return;
    const note = res.trim() || null;
    if (note === (s.note || null)) return;
    try {
      await api.setValidationNote(s.id, note);
      this.samples = this.samples.map((x) => (x.id === s.id ? { ...x, note } : x));
    } catch (e) {
      alertDialog({ title: 'Opslaan mislukt', message: e.message });
    }
  }

  async removeSample(s) {
    if (!(await confirmDialog({ title: 'Sample verwijderen', message: `"${s.filename}" uit de testset verwijderen?`, danger: true }))) return;
    try { await api.deleteValidationSample(s.id); await this.loadSamples(); }
    catch (e) { alertDialog({ title: 'Fout', message: e.message }); }
  }
  _revokeAudio() {
    // Zie analysis-page: geen blob meer, dus alleen de verwijzing legen.
    this._audioUrl = null;
  }

  /** Re-fetch the expanded sample's boorprofiel after an inline edit. */
  async _reloadDetail() {
    if (!this.expandedId) return;
    try { this.detail = await api.getValidationBoorprofiel(this.expandedId); }
    catch (_) { /* keep previous */ }
  }

  /** Add a boring by hand to the expanded sample (manual profile building). */
  async _addBoring(s) {
    try { await api.createValidationBoring(s.id); await this._reloadDetail(); }
    catch (e) { alertDialog({ title: 'Fout', message: e.message }); }
  }

  /** mv-table toggles row.expanded itself; we load the detail for the opened row. */
  async _onRowClick(e) {
    const row = e.detail.row;
    if (!row.expanded) {
      if (this.expandedId === row.id) { this.expandedId = ''; this.detail = null; this._revokeAudio(); }
      return;
    }
    // single open row at a time → keep this.detail unambiguous
    (this.samples || []).forEach(s => { if (s !== row) s.expanded = false; });
    this._revokeAudio();
    // Confirmed-but-not-tested → show the example (Verwacht); otherwise the live profile.
    this.expandedId = row.id; this.detail = null;
    this._detailTab = (row.hasGolden && !row.lastTestAt) ? 'golden' : 'current';
    this.requestUpdate();
    try { this.detail = await api.getValidationBoorprofiel(row.id); }
    catch (err) { this.detail = { error: err.message }; }
    if (row.fileType === 'audio') {
      try { this._audioUrl = api.validationAudioUrl(row.id); this.requestUpdate(); }
      catch (_) { /* no audio — fine */ }
    }
  }

  // ── Categories ────────────────────────────────────────────────────────────
  _catOf(s) { return s.category || NO_CATEGORY; }

  // Categorieën en ingeklapte secties zijn per model — elk model heeft zijn eigen testset.
  _lsKey(name) { return `mv-validation-${name}:${this.profile}`; }

  // Known categories persist (localStorage) so empty ones survive + can be removed.
  /** Wat je op deze pagina het laatst koos. Leeg = volg productie. */
  _loadKeuze() {
    try { const o = JSON.parse(localStorage.getItem(this._lsKey('test-config'))); if (o && typeof o === 'object') return o; } catch (_) {}
    return {};
  }

  /**
   * Eén veld van de testkeuze zetten en bewaren.
   *
   * Ook een LEGE waarde wordt bewaard: "geen transcript-prompt" is een keuze, en die moet een
   * actieve versie kunnen overrulen. Vandaar dat het veld in het object staat en niet dat de
   * waarde waar moet zijn.
   */
  _setOverride(veld, waarde) {
    const o = { ...this._loadKeuze(), [veld]: waarde };
    try { localStorage.setItem(this._lsKey('test-config'), JSON.stringify(o)); } catch (_) {}
    this._aiConfig = { ...this._aiConfig, [veld]: waarde };
  }

  /**
   * De categorieën die dit model kent. Per model apart (de sleutel bevat het profiel), want
   * een testset van NEN 5104 zegt niets over die van NEN 6693.
   *
   * Een LEEG opgeslagen lijstje is een geldig antwoord: dat betekent "alles weggegooid", niet
   * "nog nooit ingesteld". Stond hier `a.length`, dan kwamen de standaardcategorieën na het
   * verwijderen van de laatste gewoon weer terug.
   */
  _loadKnown() {
    try { const a = JSON.parse(localStorage.getItem(this._lsKey('known-categories'))); if (Array.isArray(a)) return a; } catch (_) {}
    return [...DEFAULT_CATEGORIES];
  }
  _saveKnown(list) {
    this._known = list;
    try { localStorage.setItem(this._lsKey('known-categories'), JSON.stringify(list)); } catch (_) {}
    this.requestUpdate();
  }

  /**
   * All ECHTE category names for the dropdown: known list first, then any in-use extras.
   * NO_CATEGORY hoort hier niet bij — dat is geen keuze maar de afwezigheid van een keuze.
   */
  _categories() {
    const known = this._known || DEFAULT_CATEGORIES;
    const ordered = [...known];
    // Categorieën die wél in gebruik zijn maar niet in het lijstje staan (bv. via een import
    // binnengekomen) horen er ook bij; anders staat er een sectie die je niet kunt kiezen.
    for (const s of (this.samples || [])) { const c = this._catOf(s); if (c !== NO_CATEGORY && !ordered.includes(c)) ordered.push(c); }
    // 'Format checks' wordt NIET afgedwongen. Het is een startsuggestie (DEFAULT_CATEGORIES),
    // geen categorie die elk model moet hebben: op NEN 6693 stond hij leeg in de keuzelijst en
    // was hij niet weg te krijgen, terwijl alle tests met die naam op NEN 5104 staan.
    return ordered.filter(c => c !== NO_CATEGORY);
  }

  _usedCategories() { return new Set((this.samples || []).map(s => this._catOf(s))); }

  /** Lege (verwijderbare) categorieën — bekend, maar zonder samples op dit model. */
  _emptyCategories() {
    const used = this._usedCategories();
    return this._categories().filter(c => !used.has(c));
  }

  /** Categories that actually have samples — these get a section. 'Geen categorie' onderaan. */
  _sectionCategories() {
    const used = this._usedCategories();
    const sections = this._categories().filter(c => used.has(c));
    if (used.has(NO_CATEGORY)) sections.push(NO_CATEGORY);
    return sections;
  }

  async _createCategory(forSample = null) {
    const name = await promptDialog({ title: 'Nieuwe categorie', message: 'Naam van de nieuwe categorie:', placeholder: 'bijv. BZB checks', confirmLabel: 'Aanmaken' });
    const n = (name || '').trim();
    if (!n) return;
    if (!this._categories().includes(n)) this._saveKnown([...(this._known || DEFAULT_CATEGORIES), n]);
    if (forSample) await this.setCategory(forSample, n);
  }

  async _deleteCategory(cat) {
    if (this._usedCategories().has(cat)) { alertDialog({ title: 'Categorie niet leeg', message: `"${cat}" bevat nog samples; verplaats die eerst.` }); return; }
    this._saveKnown((this._known || DEFAULT_CATEGORIES).filter(c => c !== cat));
  }

  async _renameCategory(cat) {
    if (cat === NO_CATEGORY) return; // verzamelbak, geen categorie
    const name = await promptDialog({ title: 'Categorie hernoemen', message: `Nieuwe naam voor "${cat}":`, value: cat, confirmLabel: 'Opslaan' });
    const n = (name || '').trim();
    if (!n || n === cat) return;
    const inCat = this.samples.filter(s => this._catOf(s) === cat);
    this.loading = true;
    try {
      for (const s of inCat) await api.setValidationCategory(s.id, n);
      this._saveKnown((this._known || DEFAULT_CATEGORIES).map(c => c === cat ? n : c));
      if (this._collapsed[cat]) { const c = { ...this._collapsed, [n]: true }; delete c[cat]; this._collapsed = c; try { localStorage.setItem(this._lsKey('collapsed'), JSON.stringify(c)); } catch (_) {} }
      await this.loadSamples();
    } catch (e) { alertDialog({ title: 'Fout', message: e.message }); }
    finally { this.loading = false; }
  }

  _catStats(samples) {
    const golden = samples.filter(s => s.hasGolden);
    const passed = golden.filter(s => s.lastTestMatch === true).length;
    const tested = golden.filter(s => s.lastTestAt).length;
    let lastRun = null;
    for (const s of samples) if (s.lastTestAt && (!lastRun || s.lastTestAt > lastRun)) lastRun = s.lastTestAt;
    return { total: samples.length, goldenCount: golden.length, passed, tested, lastRun };
  }

  /** DB-tijdstempels zijn UTC; de gedeelde helper toont ze in de tijdzone van de kijker. */
  _fmtTime(ts) {
    return ts ? formatDateTime(ts) : '';
  }

  /** Test result for a sample — live run result first, then the persisted one. */
  _resultFor(id) {
    const live = (this.report?.results || []).find(r => (r.recordingId || r.id) === id);
    if (live) return live;
    const s = (this.samples || []).find(x => x.id === id);
    if (s && s.lastTestAt) return { match: s.lastTestMatch === true, mismatches: s.lastTestMismatches || [], error: s.lastTestError || null, at: s.lastTestAt };
    return null;
  }

  /** Tabbalk: één subpagina per extractiemodel. Elk tabblad heeft zijn eigen testset. */
  _renderProfileTabs() {
    const p = this.profile;
    return html`
      <nav class="tabs">
        ${EXTRACTION_PROFILES.map(def => html`
          <a class="tab ${def.id === p ? 'on' : ''}" href="/admin/validation/${def.id}"
            @click=${(e) => { e.preventDefault(); if (def.id !== p) this._navigate(`/admin/validation/${def.id}`); }}
          >${def.label}</a>`)}
      </nav>`;
  }

  render() {
    const busy = !!this.running;
    const def = this._profile;
    return html`
      <h1>Validatie testset</h1>
      <p class="sub">AI-regressietest op een aparte database, per model. ${GELIJKTIJDIG} tests tegelijk.</p>

      ${this._renderProfileTabs()}

      <p class="sub" style="margin:1rem 0 .75rem;">
        <b style="color:var(--mv-text,#181c32);">${def.label}</b> — ${def.description}
        Samples op dit tabblad draaien alleen op dit model.
      </p>

      ${def.domain === 'afwerking' ? html`
        <div class="note">
          <b>Aangenomen boordiepte: 200 cm.</b>
          Een afwerking-sample staat hier op zichzelf, zonder lagen en dus zonder boordiepte.
          Om uitspraken als “de onderste helft is afgedicht met grond” of “gat gedicht met uitkomende grond”
          toch te kunnen toetsen, krijgt het model bij élke test op deze pagina 200 cm als boordiepte mee.
          Spreek je samples daarop in. Dit geldt <b>alleen</b> hier — bij een echte gebruiker komt de
          boordiepte altijd uit de lagen van de boring.
        </div>` : ''}

      <div class="bar" style="align-items:flex-end;">
        ${this._renderModelBar()}
        <!--
          Twee manieren om een rij te maken, en ze verschillen wezenlijk: audio levert een
          LEGE test die je hier nog moet uitwerken, een geïmporteerd bestand brengt het
          voorbeeld al mee. Dat achter één knop 'uploaden' verstoppen zou betekenen dat de
          uitkomst van je bestandsformaat afhangt.
        -->
        <div style="position:relative; margin-left:auto;" @click=${(e) => e.stopPropagation()}>
          <button class="action primary" ?disabled=${busy}
            @click=${() => { this._addMenuOpen = !this._addMenuOpen; }}
            style="display:inline-flex;align-items:center;gap:.45rem;cursor:pointer;">
            + Test toevoegen ${unsafeSVG(lucideIcon(ChevronDown, 14))}
          </button>
          ${this._addMenuOpen ? html`
            <div style="position:absolute;right:0;top:calc(100% + 6px);z-index:var(--mv-z-overlay);min-width:290px;
                        background:var(--mv-surface,#fff);border:1px solid var(--mv-border,#eff2f5);border-radius:.7rem;
                        box-shadow:0 10px 28px rgba(0,0,0,.14);padding:6px;">
              <label style=${MENU_ITEM}>
                <span style="color:var(--mv-primary,#4b9d35);display:flex;">${unsafeSVG(lucideIcon(Upload, 16))}</span>
                <span>
                  <b style="display:block;">Audio uploaden</b>
                  <span style="font-size:.78rem;color:var(--mv-text-sub,#5e6278);">Nieuwe rij; voorbeeld maak je hier</span>
                </span>
                <input type="file" multiple hidden accept="audio/*,.txt,.pdf,.docx"
                  @change=${(e) => { this._addMenuOpen = false; this._onFiles(e); }} ?disabled=${busy}>
              </label>
              <label style=${MENU_ITEM}>
                <span style="color:var(--mv-primary,#4b9d35);display:flex;">${unsafeSVG(lucideIcon(FileUp, 16))}</span>
                <span>
                  <b style="display:block;">Test importeren</b>
                  <span style="font-size:.78rem;color:var(--mv-text-sub,#5e6278);">Eerder gedownload bestand, mét voorbeeld</span>
                </span>
                <input type="file" multiple hidden accept="application/json,.json"
                  @change=${(e) => { this._addMenuOpen = false; this._onImport(e); }} ?disabled=${busy}>
              </label>
              <label style=${MENU_ITEM}>
                <span style="color:var(--mv-primary,#4b9d35);display:flex;">${unsafeSVG(lucideIcon(FolderUp, 16))}</span>
                <span>
                  <b style="display:block;">Categorie uploaden</b>
                  <span style="font-size:.78rem;color:var(--mv-text-sub,#5e6278);">Hele testset in één bestand, mét voorbeelden</span>
                </span>
                <input type="file" multiple hidden accept="application/json,.json"
                  @change=${(e) => { this._addMenuOpen = false; this._onImportCategory(e); }} ?disabled=${busy}>
              </label>
            </div>` : ''}
        </div>
      </div>
      ${this._renderProductieRegel()}

      ${this.loading && !this.samples.length
        ? html`<p class="muted">Laden…</p>`
        : !this.samples.length
          ? html`<p class="muted">Nog geen samples voor ${def.label}. Upload audio/document-bestanden om te beginnen.</p>`
          : this._sectionCategories().map(cat => this._renderCategorySection(cat))}
    `;
  }

  _bar(pct, color) {
    return html`<div style="height:8px;background:var(--mv-border,#eff2f5);border-radius:99px;overflow:hidden;max-width:440px;">
      <div style="height:100%;width:${Math.max(0, Math.min(100, pct))}%;background:${color};transition:width .25s ease;"></div></div>`;
  }

  /** Two-segment bar: green = passed, red = tested-but-failed, grey rest = untested. */
  _segBar(passed, failed, total) {
    const g = total ? (passed / total) * 100 : 0;
    const r = total ? (failed / total) * 100 : 0;
    return html`<div style="height:8px;background:var(--mv-border,#eff2f5);border-radius:99px;overflow:hidden;max-width:440px;display:flex;">
      <div style="height:100%;width:${g}%;background:var(--mv-primary,#4b9d35);transition:width .25s ease;"></div>
      <div style="height:100%;width:${r}%;background:#f1416c;transition:width .25s ease;"></div>
    </div>`;
  }

  _renderCatStatus(cat, stats) {
    const isRunning = !!this.running && this.runningCat === cat && this.progress;
    if (isRunning) {
      const p = this.progress;
      // While testing: grey bar that fills with green (passed) / red (failed) as results arrive.
      // While processing: neutral progress (no pass/fail yet).
      const bar = this.running === 'test'
        ? this._segBar(p.passed || 0, p.failed || 0, p.total)
        : this._bar(Math.round((p.current / p.total) * 100), 'var(--mv-text-muted,#b5b5c3)');
      const counts = this.running === 'test' && (p.passed || p.failed) ? ` · ${p.passed || 0} geslaagd, ${p.failed || 0} mislukt` : '';
      return html`
        <div style="margin:.1rem 0 .7rem;">
          <div style="font-size:.78rem;color:var(--mv-text-sub,#5e6278);margin-bottom:3px;">${this.running === 'test' ? 'Testen' : 'Verwerken'}… ${p.current}/${p.total}${counts}</div>
          ${bar}
          <div style="font-size:.75rem;color:var(--mv-text-sub,#5e6278);margin-top:3px;">${p.label}</div>
        </div>`;
    }
    if (!stats.goldenCount) {
      return html`<p class="muted" style="font-size:.8rem;margin:.1rem 0 .7rem;">Nog geen verwacht antwoord in deze categorie — markeer een sample als correct om te kunnen testen.</p>`;
    }
    const allOk = stats.tested > 0 && stats.passed === stats.goldenCount;
    return html`
      <div style="margin:.1rem 0 .7rem;">
        <div style="display:flex;align-items:center;gap:.6rem;max-width:440px;font-size:.8rem;margin-bottom:4px;">
          <b style="color:${stats.tested === 0 ? 'var(--mv-text-sub,#5e6278)' : allOk ? '#3d812b' : '#f1416c'};">${stats.passed}/${stats.goldenCount} geslaagd</b>
          <span class="muted" style="margin-left:auto;">${stats.lastRun ? `laatst uitgevoerd: ${this._fmtTime(stats.lastRun)}` : 'nog niet uitgevoerd'}</span>
        </div>
        ${this._segBar(stats.passed, stats.tested - stats.passed, stats.goldenCount)}
      </div>`;
  }

  _loadCollapsed() {
    try { return JSON.parse(localStorage.getItem(this._lsKey('collapsed')) || '{}') || {}; }
    catch (_) { return {}; }
  }

  _toggleCollapse(cat) {
    this._collapsed = { ...this._collapsed, [cat]: !this._collapsed[cat] };
    try { localStorage.setItem(this._lsKey('collapsed'), JSON.stringify(this._collapsed)); } catch (_) {}
  }

  _renderCategorySection(cat) {
    const busy = !!this.running;
    const samples = this.samples.filter(s => this._catOf(s) === cat);
    const stats = this._catStats(samples);
    const collapsed = !!this._collapsed[cat];
    // 'Geen categorie' is een verzamelbak, geen categorie: niet hernoembaar.
    const isNone = cat === NO_CATEGORY;
    return html`
      <section class="cat-card ${collapsed ? 'collapsed' : ''}">
        <div class="cat-head">
          <div class="cat-title">
            <button class="chevron" title=${collapsed ? 'Uitklappen' : 'Inklappen'}
              @click=${() => this._toggleCollapse(cat)}>${collapsed ? '▸' : '▾'}</button>
            <h2 @click=${() => this._toggleCollapse(cat)} style="cursor:pointer;${isNone ? 'color:var(--mv-text-sub,#5e6278);font-style:italic;' : ''}">${cat}</h2>
            ${isNone ? '' : html`
              <button class="chevron" title="Categorie hernoemen" @click=${() => this._renameCategory(cat)}
                style="display:inline-flex;align-items:center;">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
              </button>`}
            <span class="muted" style="font-size:.8rem;">${samples.length} samples · ${stats.goldenCount} met verwacht antwoord</span>
            <button class="action primary" style="margin-left:auto;" @click=${() => this.testCategory(cat)} ?disabled=${busy || !stats.goldenCount}>▶︎ Test categorie</button>
            <!--
              Testen is wat je hier de hele dag doet; verwerken en downloaden een enkele keer.
              Die twee achter een ⋯-menu houdt de kop leesbaar en zet de knop die je zoekt niet
              naast twee die je niet zoekt.
            -->
            <div style="position:relative;" @click=${(e) => e.stopPropagation()}>
              <button class="action" title="Meer acties" aria-label="Meer acties voor deze categorie"
                style="display:inline-flex;align-items:center;padding:.4rem .55rem;"
                @click=${() => { this._catMenuOpen = this._catMenuOpen === cat ? '' : cat; }}>
                ${unsafeSVG(lucideIcon(MoreHorizontal, 16))}
              </button>
              ${this._catMenuOpen === cat ? html`
                <div style="position:absolute;right:0;top:calc(100% + 6px);z-index:var(--mv-z-overlay);min-width:230px;
                            background:var(--mv-surface,#fff);border:1px solid var(--mv-border,#eff2f5);border-radius:.7rem;
                            box-shadow:0 10px 28px rgba(0,0,0,.14);padding:6px;">
                  <button style=${MENU_ITEM + 'border:none;background:none;text-align:left;font:inherit;'}
                    ?disabled=${busy} @click=${() => { this._catMenuOpen = ''; this.processCategory(cat); }}>
                    <span style="color:var(--mv-primary,#4b9d35);display:flex;">${unsafeSVG(lucideIcon(RefreshCw, 16))}</span>
                    <span>
                      <b style="display:block;">Verwerk categorie</b>
                      <span style="font-size:.78rem;color:var(--mv-text-sub,#5e6278);">Bouw de concept-boorprofielen opnieuw op</span>
                    </span>
                  </button>
                  <button style=${MENU_ITEM + 'border:none;background:none;text-align:left;font:inherit;'}
                    ?disabled=${!samples.length} @click=${() => { this._catMenuOpen = ''; this.downloadCategory(cat); }}>
                    <span style="color:var(--mv-primary,#4b9d35);display:flex;">${unsafeSVG(lucideIcon(Download, 16))}</span>
                    <span>
                      <b style="display:block;">Download categorie</b>
                      <span style="font-size:.78rem;color:var(--mv-text-sub,#5e6278);">${samples.length} sample${samples.length === 1 ? '' : 's'} + voorbeelden in één bestand</span>
                    </span>
                  </button>
                </div>` : ''}
            </div>
          </div>
          <div style="margin-top:.6rem;">${this._renderCatStatus(cat, stats)}</div>
        </div>
        ${collapsed ? '' : html`
          <mv-table
            rowDrag
            @row-drop=${(e) => this._onRowDrop(e, cat)}
            ?expandable=${true}
            .columns=${this._columns()}
            .data=${samples}
            emptyText="Geen samples in deze categorie."
            @row-click=${this._onRowClick}
            .renderDetails=${(row) => this._renderDetail(row)}
          ></mv-table>`}
      </section>`;
  }

  // Small inline-style pill — column/detail render output lives in mv-table's
  // shadow DOM, so our CSS classes don't reach it; inline styles do.
  _pill(text, kind) {
    const c = {
      gold:  ['#a9821a', '#f6c34322', '#f6c34355'],
      ok:    ['#3d812b', '#4b9d3522', 'transparent'],
      bad:   ['#f1416c', '#f1416c22', 'transparent'],
      warn:  ['#92400e', '#f59e0b24', '#f59e0b59'],
      muted: ['var(--mv-text-sub,#5e6278)', 'var(--mv-border,#eff2f5)', 'transparent'],
    }[kind] || ['#5e6278', '#eff2f5', 'transparent'];
    return html`<span style="display:inline-block;padding:1px 8px;border-radius:6px;font-size:.7rem;font-weight:800;color:${c[0]};background:${c[1]};border:1px solid ${c[2]};">${text}</span>`;
  }

  /** Lifecycle/test status pill: geen voorbeeld → concept → voorbeeld → geslaagd/afwijking. */
  _statusPill(s) {
    // Tegengehouden door de hallucinatie preventie: geen fout maar een bewuste keuze, en dat
    // moet je in de lijst kunnen zien zonder de rij open te klappen.
    const preventie = hallucinatiePreventie(s.lastTestError) || (s.status === 'error' ? hallucinatiePreventie(s.error) : null);
    if (preventie) {
      return html`<span title=${`${preventie.kort}. ${preventie.detail}`}>${this._pill('🛡 hallucinatie preventie', 'warn')}</span>`;
    }
    if (s.status === 'error') return this._pill('fout', 'bad');
    if (s.hasGolden && s.lastTestAt) {
      if (s.lastTestMatch) return this._pill('geslaagd', 'ok');
      // Hoevéél er afwijkt scheelt voor waar je begint: één verkeerde kleur is iets anders dan
      // een boorprofiel dat helemaal niet klopt. Het getal staat achter de tegel zodat je dat
      // in de lijst ziet zonder elke rij open te klappen. Geen getal bij 0 (een afwijking
      // zonder losse verschillen, bv. een fout tijdens de run) en niet bij 'geslaagd'.
      const n = (s.lastTestMismatches || []).length;
      return html`${this._pill('afwijking', 'bad')}${n ? html`<span
        title=${`${n} afwijking${n > 1 ? 'en' : ''} ten opzichte van het voorbeeld`}
        style="margin-left:.35rem;font-size:.7rem;font-weight:800;color:#f1416c;">${n}</span>` : ''}`;
    }
    if (s.hasGolden) return this._pill('voorbeeld klaar', 'gold');
    if (s.hasProfile) return this._pill('concept', 'muted');
    return this._pill('geen voorbeeld', 'muted');
  }

  _columns() {
    const btn = 'padding:.4rem .75rem;border-radius:.5rem;border:1px solid var(--mv-border,#eff2f5);background:var(--mv-surface,#fff);color:var(--mv-text,#181c32);font-weight:600;cursor:pointer;font-size:.85rem;';
    const primary = btn + 'background:var(--mv-primary,#4b9d35);color:#fff;border-color:transparent;';
    const iconBtn = 'display:inline-flex;align-items:center;justify-content:center;padding:.35rem;border-radius:.45rem;border:none;background:none;color:var(--mv-text-muted,#a1a5b7);cursor:pointer;';
    const busy = !!this.running;
    return [
      { id: 'filename', title: 'Bestand', render: (s) => html`<b style="color:var(--mv-text,#181c32);">${s.filename}</b>${s.extraCount ? html` ${this._pill(`+${s.extraCount} opname${s.extraCount > 1 ? 's' : ''}`, 'muted')}` : ''}` },
      /*
       * Eigen kolom, want dit is een ander gegeven dan de bestandsnaam. Het bestand heet
       * "memo_1787873133067.m4a" en dat zegt niemand iets; de boring heet "B03". Achter elkaar
       * op één regel lazen ze als één lange naam.
       *
       * Leeg zolang er geen voorbeeld is: dan bestaat de boring nog niet.
       */
      { id: 'boring', title: 'Boring', width: '120px',
        render: (s) => s.boringName
          ? html`<span style="font-weight:600;color:var(--mv-text,#181c32);">${s.boringName}</span>`
          : html`<span style="color:var(--mv-text-sub,#5e6278);">—</span>` },
      { id: 'status', title: 'Status', width: '120px', render: (s) => this._statusPill(s) },
      { id: '_actions', title: 'Acties', width: '1%',
        render: (s) => {
          let actions;
          if (s.hasGolden) {
            // Confirmed example → test it or wipe it.
            actions = html`
              <button style=${primary} @click=${() => this.testRow(s)} ?disabled=${busy}>▶︎ Test uitvoeren</button>
              <button style=${btn} @click=${() => this.clearGolden(s)} ?disabled=${busy}>Voorbeeld wissen</button>`;
          } else if (s.hasProfile) {
            // Draft profile ready → user reviews/edits, then confirms.
            actions = html`<button style=${primary} @click=${() => this.markGolden(s)} ?disabled=${busy}>Bevestig als correct</button>`;
          } else {
            // Fresh sample → build the example.
            actions = html`<button style=${primary} @click=${() => this.processOne(s)} ?disabled=${busy}>Maak voorbeeld aan</button>`;
          }
          return html`<div style="display:flex;gap:.4rem;white-space:nowrap;" @click=${(e) => e.stopPropagation()}>${actions}</div>`;
        } },
      { id: 'lastTest', title: 'Laatste test', width: '150px',
        render: (s) => s.lastTestAt
          ? html`<span style="font-size:.8rem;color:var(--mv-text-sub,#5e6278);">${this._fmtTime(s.lastTestAt)}</span>`
          : html`<span style="color:var(--mv-text-sub,#5e6278);">—</span>` },
      /*
       * Rijbeheer helemaal rechts, als iconen. Downloaden en verwijderen zeggen niets over de
       * staat van de test — ze stonden onder 'Acties' tussen 'Maak voorbeeld aan' en 'Test
       * uitvoeren', waar je bij het scannen van een rij juist de vólgende stap zoekt.
       */
      { id: '_row', title: '', width: '1%',
        render: (s) => html`
          <div style="display:flex;gap:.15rem;justify-content:flex-end;white-space:nowrap;"
            @click=${(e) => e.stopPropagation()} @mousemove=${tipMove} @mouseleave=${tipHide}>
            <button style="${iconBtn}${s.note ? 'color:var(--mv-primary,#4b9d35);' : ''}"
              data-tip=${s.note ? 'Notitie bekijken of bewerken' : 'Notitie toevoegen'}
              @click=${() => this.openNote(s)} ?disabled=${busy}>
              ${unsafeSVG(lucideIcon(StickyNote, 15))}
            </button>
            <button style=${iconBtn} data-tip="Test downloaden (audio + voorbeeld)"
              @click=${() => this.downloadSample(s)} ?disabled=${busy}>
              ${unsafeSVG(lucideIcon(Download, 15))}
            </button>
            <button style="${iconBtn}color:var(--mv-danger,#f1416c);" data-tip="Rij verwijderen"
              @click=${() => this.removeSample(s)} ?disabled=${busy}>
              ${unsafeSVG(lucideIcon(Trash2, 15))}
            </button>
          </div>` },
    ];
  }

  /**
   * The boorprofiel viewer — the exact same <recording-row-detail> as /projects.
   * 'current' (Gecreëerd) is editable and routes its edits to the validation DB;
   * 'golden' (Verwacht) is the frozen snapshot, shown read-only.
   */
  _renderProfile(s, tab, hasGolden, borings) {
    // De classificatiestandaard van dit model bepaalt welke codelijsten het laag-formulier toont.
    const std = this._profile.soilStandard;
    // Eén model = één tabblad: een peilbuistest hoeft geen lagen-, grondmonster- of
    // afwerkingtab. Het lagen-model houdt alle tabbladen (die boring is compleet).
    const tabs = this._profileTabs();
    // De korrelverdeling staat hier altijd aan. In een project is dat een instelling, maar een
    // validatiesample hoort bij geen project -- en de percentages zijn iets dat mensen inspreken,
    // dus moet je ze hier kunnen zien en corrigeren. Zijn ze leeg, dan staat het blok er gewoon
    // leeg bij; de backend zet de korrelverdeling voor een validatierun ook altijd aan.
    // Verwacht — the frozen example, read-only.
    if (tab === 'golden') {
      return html`<recording-row-detail boringOnly readonly showMeta grainSizeInput .standard=${std} .onlyTabs=${tabs}
        .recording=${{ id: s.id, type: s.fileType }} .borings=${borings}></recording-row-detail>`;
    }
    if (!borings.length) {
      return html`<div style="color:var(--mv-text-sub,#5e6278);font-size:.85rem;">Nog geen resultaat. Klik op <b>Maak voorbeeld aan</b>, of voeg hieronder handmatig een boring toe.</div>`;
    }
    // After an example exists, 'Gecreëerd' is a read-only comparison view with diff highlight.
    if (hasGolden) {
      const hl = this._buildHighlight(this.detail?.diff);
      return html`
        ${hl ? html`<div style="display:flex;align-items:center;gap:.4rem;font-size:.78rem;color:#f1416c;margin:0 0 .5rem;">
          <span style="width:11px;height:11px;border-radius:3px;background:#f1416c1f;border:1px solid #f1416c66;"></span>
          Rood gemarkeerd = wijkt af van Verwacht.
        </div>` : ''}
        <recording-row-detail boringOnly readonly showMeta grainSizeInput .standard=${std} .onlyTabs=${tabs}
          .recording=${{ id: s.id, type: s.fileType }} .borings=${borings} .highlight=${hl}></recording-row-detail>`;
    }
    // Concept phase — editable draft; edits go to the validation DB.
    return html`<recording-row-detail boringOnly showMeta grainSizeInput .standard=${std} .onlyTabs=${tabs}
      .recording=${{ id: s.id, type: s.fileType }}
      .borings=${borings}
      .crud=${api.validationCrud}
      @borings-changed=${() => this._reloadDetail()}></recording-row-detail>`;
  }

  /** Turn the backend diff cells into a per-boring lookup for <recording-row-detail>. */
  _buildHighlight(cells) {
    if (!cells || !cells.length) return null;
    const borings = {};
    const get = (name) => (borings[name] ||= { meta: false, metaFields: new Set(), missingLayers: 0, layers: {}, tabs: new Set() });
    for (const c of cells) {
      const b = get(c.boring);
      // metaFields = de losse kopgegeven-velden die afwijken; recording-row-detail kleurt die rood.
      if (c.scope === 'meta') { b.meta = true; if (c.column && c.column !== 'meta') b.metaFields.add(c.column); }
      else if (c.scope === 'layer') {
        const L = (b.layers[c.index] ||= { cols: new Set(), extra: false });
        if (c.kind === 'extra') L.extra = true;
        else if (c.column) L.cols.add(c.column);
      } else if (c.scope === 'tab') b.tabs.add(c.column);
      else if (c.scope === 'boring' && c.kind === 'missingLayers') b.missingLayers = c.count;
    }
    return { borings };
  }

  _renderDetail(s) {
    // renderDetails runs inside mv-table's shadow DOM → inline styles only.
    const label = 'font-size:.7rem;font-weight:800;letter-spacing:.05em;color:var(--mv-text-sub,#5e6278);margin:0 0 .4rem;text-transform:uppercase;display:flex;justify-content:space-between;align-items:center;gap:.5rem;';
    if (!this.detail || this.expandedId !== s.id) return html`<div style="color:var(--mv-text-sub,#5e6278);font-size:.85rem;">Laden…</div>`;
    if (this.detail.error) return html`<div style="font-size:.85rem;color:var(--mv-text);">Fout: ${this.detail.error}</div>`;

    const d = this.detail;
    const result = this._resultFor(s.id);
    const hasGolden = !!(d.golden && d.golden.length);
    // Without an example yet, there's only the editable draft (no Gecreëerd/Verwacht split).
    const tab = hasGolden ? (this._detailTab || 'current') : 'current';
    const borings = tab === 'golden' ? (d.golden || []) : (d.current || []);
    const seg = (on, disabled) => `padding:.4rem .85rem;border:none;font-size:.85rem;font-weight:600;cursor:${disabled ? 'not-allowed' : 'pointer'};opacity:${disabled ? .5 : 1};background:${on ? 'var(--mv-primary,#4b9d35)' : 'var(--mv-surface,#fff)'};color:${on ? '#fff' : 'var(--mv-text-sub,#5e6278)'};`;


    return html`
      <div>
        <!-- Move this sample to another category -->
        <div style="display:flex;align-items:center;gap:.5rem;font-size:.8rem;color:var(--mv-text-sub,#5e6278);margin-bottom:.7rem;">
          <span>Categorie:</span>
          <searchable-select style="min-width:220px;"
            .options=${this._categories()}
            .value=${s.category || ''}
            .deletable=${this._emptyCategories()}
            addLabel="+ Nieuwe categorie…"
            @change=${(e) => {
              // Een lege waarde is '— wissen —': dat moet de categorie leegmaken, niet
              // genegeerd worden. Alleen niets doen als er feitelijk niets verandert.
              const next = e.detail.value || null;
              if (next !== (s.category || null)) this.setCategory(s, next);
            }}
            @add=${() => this._createCategory(s)}
            @delete=${(e) => this._deleteCategory(e.detail.value)}></searchable-select>
        </div>

        <!-- Which model/prompt config the last test used -->
        ${result && s.lastTestConfig ? html`
          <div style="display:flex;flex-wrap:wrap;gap:.4rem .9rem;font-size:.76rem;color:var(--mv-text-sub,#5e6278);margin-bottom:.6rem;">
            <span><b style="font-weight:700;">Audio→tekst:</b> ${s.lastTestConfig.transcriptionModel || '—'}</span>
            ${SCHAKELAARS.filter((x) => s.lastTestConfig[x.veld] !== undefined).map((x) => html`<span><b style="font-weight:700;">${x.label}:</b> ${s.lastTestConfig[x.veld] ? 'aan' : 'uit'}</span>`)}
            <span><b style="font-weight:700;">Tekst→boorprofiel:</b> ${s.lastTestConfig.extractionModel || '—'}</span>
            <span><b style="font-weight:700;">Prompt:</b> ${s.lastTestConfig.promptTitle || '—'}</span>
          </div>` : ''}

        <!-- Top: did the test pass? -->
        ${result && hallucinatiePreventie(result.error) ? (() => {
          const p = hallucinatiePreventie(result.error);
          return html`
            <div style="border-radius:.5rem;padding:.6rem .8rem;margin-bottom:.85rem;font-size:.85rem;
              background:#f59e0b1a;color:#92400e;border:1px solid #f59e0b59;display:flex;flex-direction:column;gap:.2rem;">
              <span style="font-weight:700;">🛡 Niet verwerkt door de hallucinatie preventie</span>
              <span style="color:var(--mv-text,#181c32);">${p.detail}</span>
              <span style="font-size:.78rem;color:var(--mv-text-sub,#5e6278);">Er is geen boorprofiel gemaakt, dus ook niet vergeleken met het voorbeeld.
                Zet hallucinatie preventie in de balk bovenaan uit om te zien wat er zonder preventie uit was gekomen.</span>
            </div>`;
        })() : result ? html`
          <div style="border-radius:.5rem;padding:.55rem .8rem;margin-bottom:.85rem;font-weight:700;font-size:.85rem;
            ${result.match
              ? 'background:#4b9d3514;color:#3d812b;border:1px solid #4b9d3540;'
              : 'background:#f1416c14;color:#f1416c;border:1px solid #f1416c40;'}">
            ${result.match
              ? '✅ Test geslaagd — het boorprofiel komt 100% overeen met het verwachte resultaat.'
              : html`⚠️ Afwijking ten opzichte van het verwachte resultaat.
                  ${result.error ? html`<div style="font-family:ui-monospace,monospace;font-size:.8rem;margin:.1rem 0;">fout: ${result.error}</div>` : ''}
                  ${(result.mismatches || []).map(m => html`<div style="font-family:ui-monospace,monospace;font-size:.8rem;margin:.1rem 0;">• ${m}</div>`)}`}
          </div>` : ''}

        <div style="display:flex;gap:1.5rem;flex-wrap:wrap;align-items:flex-start;">
          <!-- Left: audio + transcript -->
          <div style="flex:1 1 300px;min-width:260px;">
            ${this._audioUrl ? html`<audio controls preload="metadata" src=${this._audioUrl} style="width:100%;margin-bottom:.7rem;"></audio>` : ''}
            <div style=${label}><span>Transcriptie${d.extraRecordings?.length ? ' (opname 1)' : ''}</span></div>
            <div style="background:var(--mv-surface,#fff);border:1px solid var(--mv-border,#eff2f5);border-radius:.5rem;padding:.65rem .8rem;font-size:.85rem;line-height:1.55;color:var(--mv-text,#181c32);">
              ${d.transcript || html`<span style="color:var(--mv-text-sub,#5e6278);">(geen transcript)</span>`}
            </div>
            ${(d.extraRecordings || []).map((ex, i) => html`
              <div style=${label + 'margin-top:.7rem;'}><span>Extra opname ${i + 2} — ${ex.filename}</span></div>
              <div style="background:var(--mv-surface,#fff);border:1px solid var(--mv-border,#eff2f5);border-radius:.5rem;padding:.65rem .8rem;font-size:.85rem;line-height:1.55;color:var(--mv-text,#181c32);">
                ${ex.transcript || html`<span style="color:var(--mv-text-sub,#5e6278);">(nog niet getranscribeerd — draai 'Verwerk')</span>`}
              </div>`)}

            <!-- Add another audio/transcript to this boring (meerdere opnames → één boorprofiel) -->
            <label style="display:inline-flex;align-items:center;gap:.4rem;margin-top:.8rem;padding:.45rem .8rem;border:1px dashed var(--mv-border,#d8dbe0);border-radius:.5rem;background:var(--mv-surface,#fff);color:var(--mv-text-sub,#5e6278);font-weight:600;cursor:${this.running ? 'not-allowed' : 'pointer'};opacity:${this.running ? .5 : 1};font-size:.82rem;">
              + Extra opname toevoegen
              <input type="file" multiple hidden accept="audio/*,.txt,.pdf,.docx" ?disabled=${!!this.running} @change=${(e) => this._onFiles(e, s.id)}>
            </label>
            <!--
              Opnieuw verwerken: bouw het concept-voorbeeld opnieuw op uit hetzelfde transcript.
              Nodig zodra je aan de prompt of het model sleutelt — anders kijk je naar een
              profiel dat met de vorige instelling is gemaakt.
            -->
            <button style="display:inline-flex;align-items:center;gap:.4rem;margin-top:.8rem;margin-left:.4rem;padding:.45rem .8rem;border:1px solid var(--mv-border,#d8dbe0);border-radius:.5rem;background:var(--mv-surface,#fff);color:var(--mv-text-sub,#5e6278);font-weight:600;font-size:.82rem;cursor:${this.running ? 'not-allowed' : 'pointer'};opacity:${this.running ? .5 : 1};"
              ?disabled=${!!this.running} @click=${() => this.reprocessOne(s)}>
              ${unsafeSVG(lucideIcon(RefreshCw, 14))} Opnieuw verwerken
            </button>
            <div style="font-size:.74rem;color:var(--mv-text-sub,#5e6278);margin-top:.35rem;">Voeg een tweede audio/transcript toe en klik daarna op <b>Verwerk</b> — alle opnames worden samengevoegd tot één boorprofiel. <b>Opnieuw verwerken</b> bouwt het concept opnieuw op met de instellingen hierboven.</div>
          </div>

          <!-- Right: boorprofiel — before an example: editable draft; after: Gecreëerd vs Verwacht -->
          <div style="flex:1 1 440px;min-width:320px;">
            <div style=${label}>
              <span>${this._profile.domain === 'lagen' ? 'Boorprofiel' : this._profile.label}</span>
              ${hasGolden ? html`
                <div style="display:inline-flex;border:1px solid var(--mv-border,#eff2f5);border-radius:.5rem;overflow:hidden;">
                  <button style=${seg(tab === 'current', false)} @click=${() => this._detailTab = 'current'}>Gecreëerd</button>
                  <button style=${seg(tab === 'golden', false)} @click=${() => this._detailTab = 'golden'}>Verwacht</button>
                </div>`
                : html`<span style="font-size:.7rem;font-weight:800;letter-spacing:.05em;color:#a9821a;">VOORBEELD · CONCEPT</span>`}
            </div>
            ${this._renderProfile(s, tab, hasGolden, borings)}
            ${!hasGolden && d.current?.length ? html`
              <div style="margin-top:.6rem;display:flex;gap:.6rem;align-items:center;flex-wrap:wrap;">
                <button style="padding:.4rem .8rem;border-radius:.5rem;border:1px dashed var(--mv-border,#d8dbe0);background:var(--mv-surface,#fff);color:var(--mv-text-sub,#5e6278);font-weight:600;cursor:pointer;font-size:.82rem;"
                  @click=${() => this._addBoring(s)} ?disabled=${!!this.running}>+ Boring toevoegen</button>
                <span style="font-size:.75rem;color:var(--mv-text-sub,#5e6278);">Corrigeer het profiel en klik dan op <b>Bevestig als correct</b> om het als verwacht vast te leggen.</span>
              </div>` : ''}
          </div>
        </div>
      </div>`;
  }
}

customElements.define('validation-page', ValidationPage);
